import {
  type CollectionType,
  createItem,
  createItems,
  customEndpoint,
  type DirectusUser,
  deleteItem,
  type NestedPartial,
  passwordRequest,
  passwordReset,
  type Query,
  type QueryFilter,
  readAssetRaw,
  readItem,
  readItems,
  readMe,
  readUser,
  readUsers,
  registerUser,
  registerUserVerify,
  updateItem,
  updateItemsBatch,
  updateMe,
} from "@directus/sdk";
import {
  type CodecEventRecord,
  type CodecEventType,
  type CodecMeshDeviceEventRecord,
  type CodecMeshDeviceEventType,
  type LicPacket,
  omit,
} from "@irriga-mais/common";
import { appConfig } from "@/utils/config";
import { cleanObject } from "@/utils/object";
import type { OmitAndMerge } from "@/utils/types";
import type { AppDirectusClient, AppSchema } from "./client";
import { createDirectusInstance } from "./client";
import { getRequestUrl } from "./get-request-url";
import type { LICPacketPayloadType } from "./model/current-lic-packet";
import type { CurrentProjectState } from "./model/current-project-state";
import type {
  IrrigationPlanExecutionReportParams,
  IrrigationPlanExecutionReportResponse,
} from "./model/irrigation-plan-execution-report";
import type { User } from "./model/user";
import {
  type AccountUserTree,
  type AccountUserWithAccount,
  getAccountUserTree,
  listAccountsWithOwners,
} from "./queries/account";

type CollectionKey = keyof AppSchema;
type CollectionItem<K extends CollectionKey = CollectionKey> = CollectionType<AppSchema, K>;

type READ<C extends CollectionKey> = {
  getOne: (id: CollectionItem<C>["id"]) => Promise<CollectionItem<C>>;
  getMany: (ids: Array<CollectionItem<C>["id"]>) => Promise<CollectionItem<C>[]>;
  query: (query: Query<AppSchema, CollectionItem<C>>) => Promise<CollectionItem<C>[]>;
};

type CRUD<C extends CollectionKey> = READ<C> & {
  create: (data: NestedPartial<CollectionItem<C>>) => Promise<CollectionItem<C>>;
  createBatch: (items: NestedPartial<CollectionItem<C>>[]) => Promise<CollectionItem<C>[]>;
  update: (id: CollectionItem<C>["id"], data: NestedPartial<CollectionItem<C>>) => Promise<CollectionItem<C>>;
  updateBatch: (items: NestedPartial<CollectionItem<C>>[]) => Promise<CollectionItem<C>[]>;
  del: (id: CollectionItem<C>["id"]) => Promise<void>;
};

/**
 * Cleans the parameters object by removing undefined values (cleanObject),
 * and transforming Date objects to ISO strings.
 * @param params
 * @param arrayHandling - Optional parameter to specify how to handle arrays. If "expand", arrays will be expanded into multiple query parameters. If "join", arrays will be joined into a single comma-separated string. If undefined, arrays will be left as is.
 * @returns A new object with cleaned and transformed parameters.
 */
type DateToIsoString<T> = T extends Date ? string : T;
type ArrayItem<T> = T extends readonly (infer U)[] ? U : never;

type SanitizedParamsDefault<P extends Record<string, unknown>> = Partial<{
  [K in keyof P]: DateToIsoString<P[K]>;
}>;

type SanitizedParamsJoin<P extends Record<string, unknown>> = Partial<{
  [K in keyof P]: P[K] extends readonly unknown[] ? string : DateToIsoString<P[K]>;
}>;

type SanitizedParamsExpand<P extends Record<string, unknown>> = Partial<{
  [K in keyof P as P[K] extends readonly unknown[] ? never : K]: DateToIsoString<P[K]>;
}> &
  Partial<{
    [K in keyof P as P[K] extends readonly unknown[] ? `${Extract<K, string>}[${number}]` : never]: ArrayItem<P[K]>;
  }>;

type SanitizedParams<P extends Record<string, unknown>, A extends "expand" | "join" | undefined> = A extends "expand"
  ? SanitizedParamsExpand<P>
  : A extends "join"
    ? SanitizedParamsJoin<P>
    : SanitizedParamsDefault<P>;

function sanitizeParams<P extends Record<string, unknown>, A extends "expand" | "join" | undefined = undefined>(
  params: P,
  arrayHandling: A = undefined as A
): SanitizedParams<P, A> {
  const cleaned = cleanObject(params);
  const transformed = Object.fromEntries(
    Object.entries(cleaned).flatMap(([key, value]) => {
      if (value instanceof Date) {
        return [[key, value.toISOString()]];
      } else if (Array.isArray(value)) {
        if (arrayHandling === "expand") {
          return value.map((v, idx) => [`${key}[${idx}]`, v]);
        } else if (arrayHandling === "join") {
          return [[key, value.join(",")]];
        }
      }
      return [[key, value]];
    })
  );

  return transformed as SanitizedParams<P, A>;
}

export class DirectusApiService {
  private static instance: DirectusApiService;

  private constructor(protected readonly client: AppDirectusClient) {}

  public static getInstance(client: AppDirectusClient = createDirectusInstance()): DirectusApiService {
    if (!DirectusApiService.instance) {
      DirectusApiService.instance = new DirectusApiService(client);
    }
    return DirectusApiService.instance;
  }

  protected read<C extends CollectionKey>(collection: C): READ<C> {
    const read: READ<C> = {
      getOne: async (id: CollectionItem<C>["id"]) => {
        return (await this.client.request(
          readItem(collection, id, {
            fields: ["*"],
          })
        )) as CollectionItem<C>;
      },
      getMany: async (IDs: Array<CollectionItem<C>["id"]>) => {
        return (await this.client.request(
          readItems(collection, {
            fields: ["*"],
            filter: {
              id: { _in: IDs },
            } as QueryFilter<AppSchema, CollectionItem<C>>,
          })
        )) as CollectionItem<C>[];
      },
      query: async (query: Query<AppSchema, CollectionItem<C>>) => {
        return (await this.client.request(readItems(collection, query))) as CollectionItem<C>[];
      },
    };
    return read;
  }

  protected crud<C extends CollectionKey>(collection: C): CRUD<C> {
    const crud: CRUD<C> = {
      ...this.read(collection),
      create: async (data: NestedPartial<CollectionItem<C>>) => {
        return (await this.client.request(createItem(collection, data as any, { fields: ["*"] }))) as CollectionItem<C>;
      },
      createBatch: async (items: NestedPartial<CollectionItem<C>>[]) => {
        return (await this.client.request(
          createItems(collection, items as any, { fields: ["*"] })
        )) as CollectionItem<C>[];
      },
      update: async (id: CollectionItem<C>["id"], data: NestedPartial<CollectionItem<C>>) => {
        return (await this.client.request(
          updateItem(collection, id, data as any, { fields: ["*"] })
        )) as CollectionItem<C>;
      },
      updateBatch: async (items: NestedPartial<CollectionItem<C>>[]) => {
        return (await this.client.request(
          updateItemsBatch(collection, items as any, { fields: ["*"] })
        )) as CollectionItem<C>[];
      },
      del: async (id: CollectionItem<C>["id"]) => {
        return await this.client.request(deleteItem(collection, id));
      },
    };
    return crud;
  }

  public readonly asset = {
    resolveUrl: (assetId: string) => {
      if (!this.client) throw new Error("Directus client is not initialized");
      const asset = readAssetRaw(assetId);
      const requestOptions = asset();
      return getRequestUrl(this.client.url, requestOptions.path, requestOptions.params);
    },
  };

  public readonly account = {
    listAccountsWithUsers: async (): Promise<AccountUserWithAccount[]> => {
      return await listAccountsWithOwners(this.client);
    },
    getAccountUserTree: async (accountUserId: string): Promise<AccountUserTree> => {
      return await getAccountUserTree(this.client, accountUserId);
    },
  };

  public readonly auth = {
    login: async (email: string, password: string) => {
      return await this.client.login({ email, password });
    },
    logout: async () => {
      return await this.client.logout();
    },
    register: async (
      email: string,
      password: string,
      firstName: string,
      lastName: string
      // cpf: string,
      // phone_number: string
    ) => {
      // Directus does not have a built-in registration endpoint.
      // You might need to create a custom endpoint or use the SDK to create a user.
      return await this.client.request(
        registerUser(email, password, {
          first_name: firstName,
          last_name: lastName,
          verification_url: appConfig.auth.verificationUrl,
          // cpf,
          // phone_number,
        } as any)
      );
    },
    verifyEmail: async (token: string) => {
      return await this.client.request(registerUserVerify(token));
    },
    requestPasswordReset: async (email: string) => {
      return await this.client.request(passwordRequest(email, appConfig.auth.resetUrl));
    },
    /**
     * Reset user password using a token received via email.
     * @param token - Token to verify the user
     * @param newPassword - New password for the user
     * @returns
     */
    resetPassword: async (token: string, newPassword: string) => {
      return await this.client.request(passwordReset(token, newPassword));
    },
    changePassword: async (newPassword: string) => {
      // Directus does not have a built-in change password endpoint using a token.
      // You might need to create a custom endpoint or use the SDK to change the password.
      // This is a placeholder implementation and may need to be adjusted based on your Directus setup.
      return await this.client.request(updateMe({ password: newPassword }));
    },
    fetchUser: async () => {
      const token = await this.client.getToken();
      if (!token) throw new Error("No token found");
      // Try to fetch the user using the SDK (assuming "users" collection)
      const rawUser = await this.client.request(
        readMe({
          fields: [
            "email",
            "first_name",
            "last_name",
            "avatar",
            "id",
            "role",
            // Custom profile fields
            "cpf",
            "phone_number",
          ] as any, // Adjust fields as needed
        })
      );
      return (
        (rawUser as OmitAndMerge<
          typeof rawUser,
          "avatar" | "role",
          { avatar: string | null; role: string | null } & {
            cpf?: string | null;
            phone_number?: string | null;
          }
        >) ?? null
      );
    },
    updateProfile: async (
      data: Partial<{
        first_name: string | null;
        last_name: string | null;
        cpf: string | null;
        phone_number: string | null;
      }>
    ) => {
      return await this.client.request(updateMe(data as any));
    },
    getToken: async () => {
      return await this.client.getToken();
    },
    setToken: async (token: string) => {
      return await this.client.setToken(token);
    },
  };

  public readonly project = this.crud("project");

  public readonly property = this.crud("property");

  public readonly propertyDevice = this.crud("property_device");

  public readonly device = this.crud("device");

  public readonly deviceMessageRequest = this.crud("device_message_request");

  public readonly reservoir = this.crud("reservoir");

  public readonly sector = this.crud("sector");

  public readonly tank = this.crud("tank");

  public readonly nutrient = this.crud("nutrient");

  public readonly sensorModel = this.crud("sensor_model");

  public readonly propertyDeviceSensorAssignment = this.crud("property_device_sensor_assignment");

  public readonly cropType = this.crud("crop_type");

  public readonly sectorCrop = this.crud("sector_crop");

  public readonly projectTank = this.crud("project_tank");

  public readonly irrigationPlanStepTank = this.crud("irrigation_plan_step_tank");

  public readonly waterPump = this.crud("water_pump");

  public readonly irrigationPlan = this.crud("irrigation_plan");

  public readonly irrigationPlanSectorMirror = this.crud("irrigation_plan_sector_mirror");

  public readonly meshDeviceMapping = this.crud("mesh_device_mapping");

  public readonly virtualDeviceConfiguration = this.crud("virtual_device_configuration");

  public readonly deviceModel = this.crud("device_model");

  // Account/User management
  public readonly accountUser = this.crud("account_user");
  public readonly user = {
    getOne: async (id: User["id"]) => {
      return (await this.client.request(
        readUser(id, {
          fields: ["*"],
        })
      )) as User;
    },
    getMany: async (IDs: Array<User["id"]>) => {
      return (await this.client.request(
        readUsers({
          filter: {
            id: { _in: IDs },
          } as QueryFilter<AppSchema, User>,
        })
      )) as User[];
    },
    query: async (query: Query<AppSchema, User>) => {
      return (await this.client.request(
        readUsers(query as unknown as Query<AppSchema, DirectusUser<AppSchema>>)
      )) as unknown as User[];
    },
  };

  public readonly currentLICPacket = this.crud("current_lic_packet");

  public readonly currentProjectState = {
    ...this.crud("current_project_state"),

    /**
     * Get current project states by property ID
     * @param propertyId - Property ID to filter by
     * @returns Promise resolving to array of current project states
     */
    getByProperty: async (propertyId: string, minPacketDate: Date | null) => {
      const now = new Date();
      return await this.client.request(
        readItems("current_project_state", {
          fields: ["*"],
          filter: Object.assign(
            {},
            minPacketDate && {
              packet_date: { _gt: minPacketDate > now ? now : minPacketDate },
            },
            { project: { property: { _eq: propertyId } } }
          ) as QueryFilter<AppSchema, CurrentProjectState>,
        })
      );
    },
  };

  public readonly currentIrrigationPlanState = this.crud("current_irrigation_plan_state");

  public readonly currentReservoirState = {
    ...this.crud("current_reservoir_state"),
    getByProperty: async (propertyId: string) => {
      return (await this.client.request(
        readItems("current_reservoir_state", {
          fields: ["*"],
          filter: {
            reservoir: { property: { _eq: propertyId } },
          } as QueryFilter<AppSchema, any>,
          limit: -1,
        })
      )) as any;
    },
  };

  public readonly licState = {
    ...this.crud("lic_state"),
    readItemsByLicId: async (params: {
      id: string;
      start_date: Date | string;
      end_date?: Date | string;
      limit?: number;
      offset?: number;
      order?: "asc" | "desc";
    }) => {
      return await this.client.request(
        customEndpoint({
          method: "GET",
          path: `history/lic-state/by-lic/${params.id}`,
          params: sanitizeParams(omit(params, "id")),
        })
      );
    },
  };

  public readonly irrigationPlanStep = {
    ...this.crud("irrigation_plan_step"),
    swapOrders: async (
      step1: Pick<CollectionItem<"irrigation_plan_step">, "id" | "order">,
      step2: Pick<CollectionItem<"irrigation_plan_step">, "id" | "order">
    ) => {
      return await this.client.request(
        updateItemsBatch("irrigation_plan_step", [
          { id: step1.id, order: step2.order },
          { id: step2.id, order: step1.order },
        ])
      );
    },
  };

  public readonly irrigationPlanState = {
    readItemsByPlanId: async ({
      irrigation_plan,
      start_date,
      end_date = new Date(),
      limit,
      offset,
      order,
    }: {
      irrigation_plan: string;
      start_date: Date | string;
      end_date?: Date | string;
      limit?: number;
      offset?: number;
      order?: "asc" | "desc";
    }) => {
      return await this.client.request(
        customEndpoint({
          method: "GET",
          path: `history/irrigation-plan-state/by-irrigation-plan/${irrigation_plan}`,
          params: sanitizeParams({
            start_date,
            end_date,
            limit,
            offset,
            order,
          }),
        })
      );
    },
    readItemsByProjectId: async ({
      project,
      start_date,
      end_date = new Date(),
      limit,
      offset,
      order,
    }: {
      project: string;
      start_date: Date | string;
      end_date?: Date | string;
      limit?: number;
      offset?: number;
      order?: "asc" | "desc";
    }) => {
      return await this.client.request(
        customEndpoint({
          method: "GET",
          path: `history/irrigation-plan-state/by-project/${project}`,
          params: sanitizeParams({
            start_date,
            end_date,
            limit,
            offset,
            order,
          }),
        })
      );
    },
  };

  public readonly irrigationPlanExecutionReport = {
    readIrrigationPlanExecutionReport: async (
      params: IrrigationPlanExecutionReportParams
    ): Promise<IrrigationPlanExecutionReportResponse["data"]> => {
      const { project_id, plan_ids, start_date, end_date, order = "asc", limit = 100, offset = 0 } = params;

      return await this.client.request(
        customEndpoint({
          method: "GET",
          path: `history/irrigation-plan-execution-report/by-project/${project_id}`,
          params: sanitizeParams({
            start_date,
            end_date,
            plan_id: plan_ids && plan_ids.length > 0 ? plan_ids.join(",") : undefined,
            order,
            limit,
            offset,
          }),
        })
      );
    },
  };

  public readonly codecEvent = {
    readItemsByCodecId: async ({
      id,
      start_date,
      end_date = new Date(),
      event_types,
      distinct_on,
      limit,
      offset,
      sort_direction,
    }: {
      id: string;
      start_date: Date | string;
      end_date?: Date | string;
      event_types?: CodecEventType[];
      distinct_on?: ("event_type" | "event_state")[];
      limit?: number;
      offset?: number;
      sort_direction?: "asc" | "desc";
    }) => {
      return await this.client.request(
        customEndpoint<CodecEventRecord<{ dateType: string; eventStateType: unknown }>[]>({
          method: "GET",
          path: `history/codec-event/by-codec/${id}`,
          params: sanitizeParams({
            start_date,
            end_date,
            event_types,
            distinct_on,
            limit,
            offset,
            sort_direction,
          }),
        })
      );
    },
    readLatestItemsByCodecId: async ({
      id,
      start_date,
      end_date = new Date(),
      event_types,
      limit,
      offset,
    }: {
      id: string;
      start_date: Date | string;
      end_date?: Date | string;
      event_types?: CodecEventType[];
      limit?: number;
      offset?: number;
    }) => {
      return await this.client.request(
        customEndpoint<CodecEventRecord<{ dateType: string; eventStateType: unknown }>[]>({
          method: "GET",
          path: `history/codec-event/by-codec/${id}`,
          params: sanitizeParams({
            start_date,
            end_date,
            event_types,
            distinct_on: "event_type",
            limit,
            offset,
            sort_direction: "desc",
          }),
        })
      );
    },
  };

  public readonly codecMeshDeviceEvent = {
    readItemsByCodecId: async (params: {
      id: string;
      start_date: Date | string;
      end_date?: Date | string;
      event_types?: CodecMeshDeviceEventType[];
      distinct_on?: (
        | "sector"
        | "project"
        | "property"
        | "account"
        | "irrigation_plan"
        | "irrigation_plan_step"
        | "water_pump"
        | "mesh_device"
        | "device_idx"
        | "event_type"
        | "event_state"
      )[];
      sector?: string;
      project?: string;
      property?: string;
      account?: string;
      irrigation_plan?: string;
      irrigation_plan_step?: string;
      water_pump?: string;
      mesh_device?: string;
      device_idx?: number;
      event_state?: unknown;
      limit?: number;
      offset?: number;
      sort_direction?: "asc" | "desc";
    }) => {
      return await this.client.request(
        customEndpoint<CodecMeshDeviceEventRecord<{ dateType: string; eventStateType: unknown }>[]>({
          method: "GET",
          path: `history/codec-mesh-device-event/by-codec/${params.id}`,
          params: sanitizeParams(
            omit(
              {
                ...params,
                event_types: params.event_types?.join(","),
              },
              "id"
            )
          ),
        })
      );
    },
    readLatestItemsByCodecId: async (params: {
      id: string;
      start_date: Date | string;
      end_date?: Date | string;
      event_types?: CodecMeshDeviceEventType[];
      sector?: string;
      project?: string;
      property?: string;
      account?: string;
      irrigation_plan?: string;
      irrigation_plan_step?: string;
      water_pump?: string;
      mesh_device?: string;
      device_idx?: number;
      event_state?: unknown;
      limit?: number;
      offset?: number;
    }) => {
      return await this.client
        .request(
          customEndpoint<CodecMeshDeviceEventRecord<{ dateType: string; eventStateType: unknown }>[]>({
            method: "GET",
            path: `history/codec-mesh-device-event/by-codec/${params.id}`,
            params: sanitizeParams({
              ...omit(
                {
                  ...params,
                  event_types: params.event_types?.join(","),
                },
                "id"
              ),
              distinct_on: "mesh_device,device_idx",
              sort_direction: "desc",
            }),
          })
        )
        .then((records) => {
          return records.sort((a, b) => {
            const dateA = new Date(a.start_time).getTime();
            const dateB = new Date(b.start_time).getTime();
            return dateA - dateB;
          });
        });
    },
  };

  public readonly licPacketHistory = {
    readItemsByLicId: async (params: {
      id: string;
      start_date: Date | string;
      end_date?: Date | string;
      payload_types?: LICPacketPayloadType[];
      limit?: number;
      offset?: number;
      order?: "asc" | "desc";
    }) => {
      const filteredParams = sanitizeParams(omit(params, "id"), "expand");
      return await this.client.request<LicPacket[]>(
        customEndpoint({
          method: "GET",
          path: `history/lic-packet/by-lic/${params.id}`,
          params: filteredParams,
        })
      );
    },
  };
}

export const apiService = DirectusApiService.getInstance();
Object.assign(window, { apiService }); // For debugging purposes
